// ✅ React
import React from "react";
import { Link } from "react-router-dom/cjs/react-router-dom";

// ✅ Ant Design
import { Tooltip } from "antd";
import { UserOutlined, InfoCircleOutlined } from "@ant-design/icons";

// ✅ Third-party Libraries
import dayjs from "dayjs";

// ✅ Custom UI Components
import H3Text from "@Uilib/h3Text";
import ViewCustomFields from "@Components/Common/CustomField/ViewCustomFields";

// ✅ Helpers / Constants
import Helpers from "../../../../../apis/helpers";
import { toISTDate } from "../../../../../apis/constants";

function ViewCreditNoteDetails({
  currentCN,
  isReturnSlip,
  user,
  isTallyConnected
}) {
  return (
    <div className="document-header">
      <div className="ant-row">
        <div className="ant-col-md-24">
          <H3Text
            text={
              <span>
                <UserOutlined />{" "}
                <span>{`${currentCN?.internal_cust_code} - ${currentCN?.customer_info?.customer_name}`}</span>
              </span>
            }
            className="document-header__party-name"
          />
        </div>
        {currentCN?.invoice_number &&
          Helpers.getPermission(
            Helpers.permissionEntities.CREDIT_NOTE,
            Helpers.permissionTypes.READ,
            user
          ) && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text={isReturnSlip ? "CO #" : "Invoice #"}
                className="document-header__field-name"
              />
              <Link
                to={
                  isReturnSlip
                    ? `/sales/consumption/view/${currentCN?.invoice_id}`
                    : `/sales/invoice/view/${currentCN?.invoice_id}`
                }
                target="_blank"
              >
                <H3Text
                  text={
                    currentCN?.invoice_number || "-"
                  }
                  className="document-header__field-value"
                />
              </Link>
            </div>
          </div>
        )}
        <div className="ant-col-md-12">
          <Tooltip title={currentCN?.tenant_info?.tenant_name}>
            <div className="document-header__field">
              <H3Text
                text="Location"
                className="document-header__field-name"
              />
              <H3Text
                text={
                  currentCN?.tenant_info?.tenant_name?.length > 25
                    ? `${currentCN?.tenant_info?.tenant_name?.slice(
                      0,
                      25
                    )}..`
                    : currentCN?.tenant_info?.tenant_name
                }
                className="document-header__field-value"
              />
            </div>
          </Tooltip>
        </div>
        <div className="ant-col-md-12">
          <div className="document-header__field">
            <H3Text
              text="Department"
              className="document-header__field-name"
            />
            <H3Text
              text={currentCN?.department_name}
              className="document-header__field-value"
            />
          </div>
        </div>
        <div className="ant-col-md-12">
          <div className="document-header__field">
            <H3Text
              text={isReturnSlip ? "RS Date" : "CN Date"}
              className="document-header__field-name"
            />
            <H3Text
              text={
                currentCN?.cn_date
                  ? dayjs(currentCN?.cn_date).format("DD/MM/YYYY")
                  : "-"
              }
              className="document-header__field-value"
            />
          </div>
        </div>
        {currentCN?.reason && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text="Reason"
                className="document-header__field-name"
              />
              <H3Text
                text={currentCN?.reason}
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        {currentCN?.conversion_rate && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text="Conversion Rate"
                className="document-header__field-name"
              />
              <H3Text
                text={`${currentCN?.conversion_rate}`}
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        {currentCN?.account_manager_info && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text="Sales Manager"
                className="document-header__field-name"
              />
              <H3Text
                text={`${currentCN?.account_manager_info?.first_name} ${currentCN?.account_manager_info?.last_name}`}
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        {currentCN?.customer_info?.gst_number && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text="GSTIN"
                className="document-header__field-name"
              />
              <H3Text
                text={currentCN?.customer_info?.gst_number}
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        {currentCN?.zoho_updated_at && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text="Zoho Sync"
                className="document-header__field-name"
              />
              <H3Text
                text={
                  <a
                    href={`https://books.zoho.in/app#/creditnotes/${currentCN?.zoho_cn_id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {toISTDate(currentCN?.zoho_updated_at).format(
                      "DD/MM/YYYY hh:mm A"
                    ) || "-"}
                  </a>
                }
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        {!isReturnSlip &&
          currentCN?.integration_busy_config?.status ===
          "CONNECTED" &&
          ["CONFIRMED"].includes(currentCN?.status) && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text="Busy Status"
                className="document-header__field-name"
              />
              {currentCN?.busy_voucher_id && (
                <H3Text
                  text={
                    <React.Fragment>
                      <span className="status-tag">Pushed</span>
                    </React.Fragment>
                  }
                  className="document-header__field-value"
                />
              )}
              {currentCN?.busy_sync_error && (
                <H3Text
                  text={
                    <React.Fragment>
                      <span
                        className="status-tag"
                        style={{
                          backgroundColor: "rgb(255, 0, 0)",
                        }}
                      >
                          Error
                      </span>
                        &nbsp;
                      <Tooltip
                        title={currentCN?.busy_sync_error}
                        color="rgb(234, 242, 254)"
                      >
                        <InfoCircleOutlined
                          style={{
                            color: "rgb(255, 0, 0)",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </React.Fragment>
                  }
                  className="document-header__field-value"
                />
              )}
              {!currentCN?.busy_voucher_id &&
                  !currentCN?.busy_sync_error && (
                <H3Text
                  text={
                    <React.Fragment>
                      <span
                        className="status-tag"
                        style={{
                          backgroundColor: "rgb(70, 139, 247)",
                        }}
                      >
                            Not Synced
                      </span>
                    </React.Fragment>
                  }
                  className="document-header__field-value"
                />
              )}
            </div>
          </div>
        )}
        {!isReturnSlip &&
          isTallyConnected &&
          !currentCN?.tally_warning_message &&
          currentCN?.tally_updated_at && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text="Tally Status"
                className="document-header__field-name"
              />
              <H3Text
                text={`Pushed on ${toISTDate(
                  currentCN?.tally_updated_at
                ).format("DD MMM, YYYY hh:mm A")}`}
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        {currentCN?.tenant_id === user?.tenant_info?.tenant_id &&
          user?.tenant_info?.it_id &&
          !isReturnSlip && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text="Credit Note Account"
                className="document-header__field-name"
              />
              <div className="document-header__field-input">
                <H3Text
                  text={currentCN?.tally_credit_note_account_name}
                  className="document-header__field-value"
                />
              </div>
            </div>
          </div>
        )}
        {!isReturnSlip &&
          !currentCN?.tally_updated_at &&
          isTallyConnected &&
          !currentCN?.tally_warning_message && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text="Tally Status"
                className="document-header__field-name"
              />
              <H3Text
                text="Not Synced"
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        {!isReturnSlip &&
          currentCN?.tally_warning_message &&
          isTallyConnected && (
          <div className="ant-col-md-12">
            <div className="document-header__field">
              <H3Text
                text="Tally Status"
                className="document-header__field-name"
              />
              <H3Text
                text={
                  <React.Fragment>
                    <span
                      className="status-tag"
                      style={{
                        backgroundColor: "rgb(255, 0, 0)",
                      }}
                    >
                        Error
                    </span>
                      &nbsp;
                    <Tooltip
                      title={currentCN?.tally_warning_message}
                    >
                      <InfoCircleOutlined
                        style={{
                          color: "rgb(255, 0, 0)",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  </React.Fragment>
                }
                className="document-header__field-value"
              />
            </div>
          </div>
        )}
        {currentCN?.custom_fields?.length > 0 ? (
          <ViewCustomFields
            customFields={currentCN?.custom_fields?.filter(
              (cf) => cf.is_active
            )}
            wrapperClass="document-header__field"
            labelClass="document-header__field-name"
            valueClass="document-header__field-value"
            isHorizontal
            entityType="CREDIT_NOTE"
          />
        ) : (
          ""
        )}
      </div>
    </div>
  )
}

export default ViewCreditNoteDetails;